/** @jsxImportSource @emotion/react */
import { DATADOG_ACTIONS } from '@app/app-constants'
import SelectionTool from '@app/components/Pages/Map/MapTools/SelectionTool'
import { drawAreasAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import {
  clientLocationAtom,
  flyToClientLocationAtom,
  mapPaddingIsChangingAtom,
  riskMapInteractiveAtom,
  riskMapIs3DAtom,
  riskMapStyleAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { useRiskFilterState } from '@app/risk-migration/hooks/useRiskFilterState'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import type { ViewOrientation } from '@app/types/layerTypes'
import { logDataDogAction } from '@app/utils/logger'
import { SerializedStyles } from '@emotion/react'
import { Box } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useAtomValue, useSetAtom } from 'jotai'
import mapboxgl, { LngLat, Map, NavigationControl } from 'mapbox-gl'
import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { addBaseMapStyle } from '../RiskMapView/RiskMapView.utilities'
import './GenericMapTooltip.styles.css'
import { mapViewContainer } from './GenericMapView.styles'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

export const GenericMapView = ({
  map,
  inContainerSlot,
  outsideContainerSlot,
  onLoadFunction,
  customStyle,
  setMapInstance,
  mapPaddingRef,
  flyToClientLocation = true,
}: {
  map: Map | null
  inContainerSlot?: React.ReactNode
  outsideContainerSlot?: React.ReactNode
  onLoadFunction?: (map: Map) => void
  customStyle?: SerializedStyles
  setMapInstance: (map: Map | null) => void
  mapPaddingRef: MutableRefObject<{
    top: number
    right: number
    bottom: number
    left: number
  }>
  flyToClientLocation?: boolean
}) => {
  mapboxgl.accessToken = MAPBOX_TOKEN

  const drawAreas = useAtomValue(drawAreasAtom)
  const style = useAtomValue(riskMapStyleAtom)
  const { layers } = useRiskLayersState()

  const mapContainer = useRef(null)
  const clientLocation = useAtomValue(clientLocationAtom)
  const mapPaddingIsChanging = useAtomValue(mapPaddingIsChangingAtom)
  const mapIs3D = useAtomValue(riskMapIs3DAtom)
  const setMapIs3D = useSetAtom(riskMapIs3DAtom)
  const interactive = useAtomValue(riskMapInteractiveAtom)
  const setFlyToClientLocation = useSetAtom(flyToClientLocationAtom)

  const [viewOrientation, setViewOrientation] = useState<ViewOrientation>({
    center: new LngLat(clientLocation.longitude, clientLocation.latitude),
    zoom: clientLocation.zoom,
    pitch: 0,
    bearing: 0,
    maxPitch: 85,
  })

  const { bearing, center, pitch, zoom } = viewOrientation
  const left_offset = Math.max(mapPaddingRef.current.left + 4, 4)
  const right_offset = Math.max(mapPaddingRef.current.right + 4, 4)
  const left_changing = mapPaddingIsChanging.left
  const right_changing = mapPaddingIsChanging.right

  function addMapControls(map: Map) {
    if (interactive) map.addControl(new NavigationControl({ visualizePitch: true }))
    map.addControl(new mapboxgl.ScaleControl({ unit: 'metric', maxWidth: 200 }))
  }

  function handleMapMove(map: Map) {
    const updatedLongitude = +map.getCenter().lng.toFixed(4)
    const updatedLatitude = +map.getCenter().lat.toFixed(4)
    setViewOrientation({
      zoom: +map.getZoom().toFixed(2),
      center: new LngLat(updatedLongitude, updatedLatitude),
      pitch: +map.getPitch().toFixed(2),
      bearing: +map.getBearing().toFixed(2),
    })
    setMapIs3D(map.getPitch() > 5)
  }

  function initiateMap(mapContainer: HTMLElement) {
    const mapbox = new Map({
      container: mapContainer,
      maxPitch: 85,
      style,
      center,
      bearing,
      pitch,
      zoom,
      attributionControl: false,
    })
    if (mapPaddingRef.current) {
      mapbox.setPadding(mapPaddingRef.current)
    }

    mapbox.addControl(new mapboxgl.AttributionControl(), 'bottom-left')

    mapbox.on('style.load', async () => {
      if (drawAreas.length) SelectionTool.reAddToMap(mapbox)
      addBaseMapStyle(mapbox, style)
      if (onLoadFunction) onLoadFunction(mapbox)
    })

    mapbox.on('load', async () => {
      /* Store the map instance on state to be used by other functions */
      setMapInstance(mapbox)

      // Count billed map loads
      logDataDogAction(DATADOG_ACTIONS.MAPBOX_MAP_LOADED, {
        context: 'web',
      })
    })

    mapbox.on('moveend', () => {
      handleMapMove(mapbox)
    })
    return mapbox
  }

  // Log layer ids to Sentry
  useEffect(() => {
    const layerIds = [
      ...layers.map((l) =>
        l.layerType == 'asset' ? l.type : l.layerType == 'hazard' ? l.assetTag : l.id,
      ),
    ]
    Sentry.addBreadcrumb({ type: 'debug', message: `map layers`, data: { layerIds: layerIds } })
  }, [layers])

  /* First render or when mapStyle changes */
  useEffect(() => {
    if (!mapContainer.current) return
    setFlyToClientLocation(flyToClientLocation)
    const mapbox = initiateMap(mapContainer.current)
    addMapControls(mapbox)

    return () => {
      mapbox.remove()
      setMapInstance(null)
    }
    /* useEffect dependencies are ignored in order to work with Mapbox render cycles */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style])

  /* Re-measure the map whenever its container changes size (e.g. the side panel
     collapses/expands). Mapbox only sizes its canvas on window resize or map
     recreation, so without this the canvas is left stale/undersized. */
  useEffect(() => {
    if (!map || !mapContainer.current) return
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        try {
          map.resize()
        } catch {
          // map may have been removed before the frame ran
        }
      })
    })

    resizeObserver.observe(mapContainer.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [map, mapContainer])

  const { setDrawAreas } = useRiskFilterState()

  useEffect(() => {
    return () => {
      setDrawAreas([])
    }
  }, [setDrawAreas])

  const logMapLocation = () => {
    if (!map) return
    if (!viewOrientation.center) return
    const center = viewOrientation.center as LngLat
    // eslint-disable-next-line no-console
    console.log({
      latitude: center.lat,
      longitude: center.lng,
      zoom: viewOrientation.zoom,
      pitch: viewOrientation.pitch,
      bearing: viewOrientation.bearing,
    })

    // eslint-disable-next-line no-console
    console.log(
      'asset layers:',
      layers
        .filter((layer) => layer.layerType == 'asset')
        .map((layer) => layer.type)
        .join(','),
    )
    // eslint-disable-next-line no-console
    console.log(
      'hazard layers:',
      layers
        .filter((layer) => layer.layerType == 'hazard')
        .map((layer) => layer.assetTag)
        .join(','),
    )
    // eslint-disable-next-line no-console
    console.log(
      'info layers:',
      layers
        .filter((layer) => layer.layerType == 'information')
        .map((layer) => layer.id)
        .join(','),
    )
  }

  return (
    <>
      <Box
        css={[
          mapViewContainer(left_offset, right_offset, left_changing, right_changing, mapIs3D),
          customStyle,
        ]}
        onAuxClick={logMapLocation}
      >
        <div ref={mapContainer} style={{ height: '100%' }} />
        {inContainerSlot}
      </Box>
      {outsideContainerSlot}
    </>
  )
}
